import React, {useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Popover} from 'antd';

import {useStores} from '@src/components/store';
import {DisableReasons} from '@src/utils/enums';
import {TooltipDisableReason} from '@src/components/shared/tooltip-disable-message/tooltip-disable-reason';
import {ProtectionType} from './deactivate-version.config';
import {useDeactivateVersion} from './useDeactivateVersion';
import './deactivate-version.scss';

interface DeactivateVersionButtonProps {
    protectionType: ProtectionType;
    closePanel?: () => void;
    className?: string;
    disabled?: boolean;
}

interface DeactivateVersionProps {
    protectionType: ProtectionType;
    closePanel?: () => void;
    popoverIcon: React.ReactNode;
}

/**
 * Renders just the deactivate/reactivate action button.
 * Use this when embedding inside an existing menu or popover.
 */
export const DeactivateVersionButton: React.FC<DeactivateVersionButtonProps> = observer(({
                                                                                             protectionType,
                                                                                             closePanel,
                                                                                             className,
                                                                                             disabled,
                                                                                         }) => {
    const {userStore} = useStores();
    // pass closePanel into the hook so it can close the panel after modal success
    const {
        isScheduledVersionUpcoming,
        icon,
        label,
        contextHolders,
        openModal
    } = useDeactivateVersion(protectionType, closePanel);

    const isReadOnlyUser = userStore.isReadOnlyUser;
    const isDisabled = (disabled ?? isScheduledVersionUpcoming) || isReadOnlyUser;

    const handleClick = () => {
        openModal();
    };

    const button = (
        <button
            type="button"
            className={className ?? 'deactivate-version-wrapper'}
            disabled={isDisabled}
            onClick={handleClick}
        >
            {icon}
            <span>{label}</span>
        </button>
    );

    return (
        <>
            {contextHolders}
            {isReadOnlyUser ? (
                <TooltipDisableReason disableReason={DisableReasons.READ_ONLY_USER}>
                    {button}
                </TooltipDisableReason>
            ) : button}
        </>
    );
});

export const DeactivateVersion: React.FC<DeactivateVersionProps> = observer(({
                                                                                 protectionType,
                                                                                 closePanel,
                                                                                 popoverIcon
                                                                             }) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const handleClose = () => {
        setIsPopoverOpen(false);
    };

    return (
        <Popover
            placement="bottomRight"
            overlayClassName="popover-overlay-menu deactivate-version-menu"
            content={
                <DeactivateVersionButton
                    protectionType={protectionType}
                    closePanel={handleClose}
                    className="deactivate-version-wrapper"
                />
            }
            open={isPopoverOpen}
            trigger="click"
            showArrow={false}
            onOpenChange={() => setIsPopoverOpen(!isPopoverOpen)}
        >
            <div className="deactivate-version-popover-wrapper">
                {popoverIcon}
            </div>
        </Popover>
    );
});
